import { Controller } from "@hotwired/stimulus"
import { calculate } from "../modules/salary_lab/calculator_api"
import { formatCurrency, formatPercent, parseNumber } from "../modules/salary_lab/formatter"
import { SmartDebounce, detectEventType } from "../modules/salary_lab/smart_debounce"
import { writeGenericToUrl, readGenericFromUrl } from "../modules/salary_lab/url_state"
import { trackCalculatorUsed } from "../modules/salary_lab/ga4_tracker"

const URL_MAP = { gross_salary: "s", kiwisaver_rate: "ks", hours_per_week: "hrs", working_weeks: "weeks", training_budget: "training", equipment_budget: "equip" }
const URL_TYPES = { gross_salary: "number", hours_per_week: "number", working_weeks: "number", training_budget: "number", equipment_budget: "number" }

export default class extends Controller {
  // Values are wired from the view using server-side constants —
  // see Custom::SalaryLab::CalculatorsController#set_salary_lab_constants. No hardcoded
  // defaults here; see comment in salary_lab_comparison_controller.js
  // for the rationale.
  static values = {
    defaultWeeks: Number,
    defaultHours: Number
  }

  static targets = [
    "salaryInput", "kiwisaverSelect", "trainingInput", "equipmentInput",
    "workingWeeksInput", "hoursInput",
    "advancedOptions", "advancedToggle",
    "resultsPanel", "loadingIndicator", "introContent",
    "totalCost", "overheadAmount", "overheadPercent",
    "equivalentRate",
    "breakdownBody",
    "annualTotal", "monthlyTotal", "fortnightlyTotal", "weeklyTotal",
    "liveRegion"
  ]

  connect() {
    this._debounce = new SmartDebounce(() => this._performCalculation(), { minDigits: 4 })
    this._prefillFromUrl()
  }

  disconnect() {
    this._debounce.cancel()
  }

  inputChanged(event) {
    this._debounce.schedule(detectEventType(event), this.salaryInputTarget.value)
  }

  optionChanged() {
    this._debounce.schedule("toggle", this.salaryInputTarget.value)
  }

  toggleAdvanced(event) {
    event.preventDefault()
    const visible = this.advancedOptionsTarget.classList.toggle("hidden")
    this.advancedToggleTarget.textContent = visible ? "Show more options" : "Hide options"
  }

  submitForm(event) {
    event.preventDefault()
    this._debounce.schedule("submit")
  }

  // --- Private ---

  async _performCalculation() {
    const salary = parseNumber(this.salaryInputTarget.value)
    if (!salary || salary <= 0) return

    this._showLoading()

    try {
      const params = {
        type: "employer_cost",
        gross_salary: salary,
        kiwisaver_rate: this.kiwisaverSelectTarget.value,
        hours_per_week: this.hasHoursInputTarget ? this.hoursInputTarget.value : this.defaultHoursValue,
        working_weeks: this.hasWorkingWeeksInputTarget ? this.workingWeeksInputTarget.value : this.defaultWeeksValue
      }

      if (this.hasTrainingInputTarget) {
        params.training_budget = parseNumber(this.trainingInputTarget.value) || 0
      }
      if (this.hasEquipmentInputTarget) {
        params.equipment_budget = parseNumber(this.equipmentInputTarget.value) || 0
      }

      const data = await calculate(params)
      this._renderResults(data)
      writeGenericToUrl(params, URL_MAP)
      trackCalculatorUsed("employer_cost", { salaryAmount: salary })
    } catch (error) {
      if (this.hasLiveRegionTarget) {
        this.liveRegionTarget.textContent = error.message || "Calculation failed. Please try again."
      }
    }
  }

  _renderResults(data) {
    this.totalCostTarget.textContent = formatCurrency(data.total_employment_cost)
    this.overheadAmountTarget.textContent = formatCurrency(data.overhead_amount)
    this.overheadPercentTarget.textContent = `${data.overhead_percentage}%`
    this.equivalentRateTarget.textContent = `${formatCurrency(data.equivalent_contractor_rate)}/hr`

    // Breakdown table
    this.breakdownBodyTarget.innerHTML = data.cost_breakdown.map(item => {
      const typeClass = this._typeClass(item.type)
      return `<tr class="border-b border-gray-100 last:border-0">
        <td class="py-2 text-sm text-gray-700">${item.label}</td>
        <td class="py-2 text-sm text-right tabular-nums font-medium ${typeClass}">
          ${formatCurrency(item.amount)}
        </td>
      </tr>`
    }).join("")

    // Frequencies
    if (data.frequencies) {
      if (this.hasAnnualTotalTarget) this.annualTotalTarget.textContent = formatCurrency(data.frequencies.annual)
      if (this.hasMonthlyTotalTarget) this.monthlyTotalTarget.textContent = formatCurrency(data.frequencies.monthly)
      if (this.hasFortnightlyTotalTarget) this.fortnightlyTotalTarget.textContent = formatCurrency(data.frequencies.fortnightly)
      if (this.hasWeeklyTotalTarget) this.weeklyTotalTarget.textContent = formatCurrency(data.frequencies.weekly)
    }

    this._showResults()

    // ARIA
    if (this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent =
        `Total employment cost: ${formatCurrency(data.total_employment_cost)} per year. ` +
        `Overhead: ${formatCurrency(data.overhead_amount)} (${data.overhead_percentage}%).`
    }
  }

  _typeClass(type) {
    switch (type) {
    case "salary": return "text-gray-900"
    case "mandatory": return "text-amber-600"
    case "leave": return "text-orange-600"
    case "optional": return "text-gray-500"
    default: return "text-gray-900"
    }
  }

  _prefillFromUrl() {
    const p = readGenericFromUrl(URL_MAP, URL_TYPES)
    if (!p.gross_salary) return
    this.salaryInputTarget.value = p.gross_salary
    if (p.kiwisaver_rate != null) this.kiwisaverSelectTarget.value = p.kiwisaver_rate
    if (p.hours_per_week != null && this.hasHoursInputTarget) this.hoursInputTarget.value = p.hours_per_week
    if (p.working_weeks != null && this.hasWorkingWeeksInputTarget) this.workingWeeksInputTarget.value = p.working_weeks
    if (p.training_budget != null && this.hasTrainingInputTarget) this.trainingInputTarget.value = p.training_budget
    if (p.equipment_budget != null && this.hasEquipmentInputTarget) this.equipmentInputTarget.value = p.equipment_budget
    if (this.element.dataset.prerendered !== "true") this._performCalculation()
  }

  _showLoading() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.remove("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.add("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }

  _showResults() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.add("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.remove("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }
}
